import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import Icon            from 'app/components/common/icon';
import Link            from 'app/components/common/link';
import Select          from 'app/components/common/select';
import {
  EmployeeRoles as Roles,
  VolEventTypes,
}                      from 'app/constants';
import compHelpers     from 'app/helpers/companies';
import history         from 'app/history';
import Metrics         from 'app/metrics';
import paths           from 'app/paths';
import CadminSlx       from 'app/selectors/company-admin/';
import FfSlx           from 'app/selectors/feature-flags';

class CadminNav extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      pinned: false,
    };

    this.onClickBurger = this.onClickBurger.bind(this);
  }

  get isGmOnly() {
    return compHelpers.isGmOnly(this.props.company);
  }

  getMenuItems() {
    const {company, activeItem, currentUser, ffCattrs, ffGrants, attnNums} = this.props;
    const role = _.get(currentUser, 'employment.employee.role', Roles.EMPLOYEE);
    const is = (itemName) => itemName === activeItem;
    const showSubx = true;
    const attnDons = _.sum([attnNums?.pendingMatchInquiries, attnNums?.pendingOfflineDonations, attnNums?.pendingMatches]) || 0;
    const slug = company.slug;

    const topLevelAnalPresets = [Metrics.presets.impact, Metrics.presets.engagement, Metrics.presets.employees];
    const givingPreset = Metrics.presets.giving;
    const volPreset    = Metrics.presets.volunteering;
    const campPreset   = Metrics.presets.campaigns;
    const gmPreset     = Metrics.presets.brackets;

    const menuItems = [
      ...(!this.isGmOnly ? [
        {label: 'Dashboard', Icon: Icon.LayoutDashboard, children: [
          {label: 'Dashboard', href: paths.cadminDashboardView(slug, company.companyDashboardId), active: is('dashboard-view'), roles: [Roles.FINANCE, Roles.CHAMPION, Roles.GROUP_LEAD, Roles.IT]},
          {label: 'Dashboard Builder', href: paths.cadminDashboardEdit(slug, company.companyDashboardId), active: is('dashboard-edit')},
        ]},
      ] : []),
      {label: 'Analytics', Icon: Icon.AnalyticsBars, children: topLevelAnalPresets.map((preset) => (
        {label: preset.name, href: paths.cadminAnalytics(slug, {}, preset.key), active: is(`anal-${preset.key}`)}
      ))},
      {label: 'Donations', Icon: Icon.PaginateFilterHeart, children: [
        {label: 'Donations', attn: attnDons, href: paths.cadminMatches(slug), active: is('matches')},
        ...(company.features.match ? [
          {label: 'Match Settings', href: paths.cadminMatchSettings(slug), active: is('match-settings')},
        ] : []),
        ...(company.features.grant ? [
          {label: 'Grants', href: paths.cadminGrants(slug), active: is('grants')},
        ] : []),
        {label: 'Analytics', href: paths.cadminAnalytics(slug, {}, givingPreset.key), active: is(`anal-${givingPreset.key}`)},
      ]},
      ...(company.features.vol ? [
        {label: 'Volunteering', Icon: Icon.UserMultipleNeutral2, children: [
          {label: 'Events', attn: (attnNums?.pendingVolEvents || 0), href: paths.cadminVolEvents(slug, {type: VolEventTypes.VOL_OPP}), active: is('vol-events'), roles: [Roles.CHAMPION, Roles.GROUP_LEAD]},
          {label: 'Volunteers & Hours', attn: (attnNums?.pendingVolTimeEntries || 0), href: paths.cadminVolTracking(slug, {eventType: VolEventTypes.VOL_OPP}), active: is('vol-tracking'), roles: [Roles.GROUP_LEAD]},
          {label: 'Dollars for Doers', href: paths.cadminVolDfd(slug), active: is('vol-dfd')},
          {label: 'Analytics', href: paths.cadminAnalytics(slug, {}, volPreset.key), active: is(`anal-${volPreset.key}`)},
        ]},
      ] : []),
      ...(company.features.match ? [
        {label: 'Campaigns', Icon: Icon.Megaphone1, children: [
          {label: 'Campaigns', attn: (attnNums?.pendingCampaigns || 0), href: paths.cadminCampaigns(slug), active: is('campaigns'), roles: [Roles.CHAMPION, Roles.GROUP_LEAD]},
          ...(company.features.drive ? [
            {label: 'Drive Tracking', href: paths.cadminDriveDonations(slug), active: is('drive-donations'), roles: [Roles.CHAMPION, Roles.GROUP_LEAD]},
          ] : []),
          {label: 'Templates', href: paths.cadminCampaignTemplates(slug), active: is('campaign-templates'), roles: [Roles.CHAMPION, Roles.GROUP_LEAD]},
          {label: 'Analytics', href: paths.cadminAnalytics(slug, {}, campPreset.key), active: is(`anal-${campPreset.key}`)},
        ]},
      ] : []),
      ...(ffGrants ? [
        {label: 'Grants', Icon: Icon.TaskChecklistCheck, children: [
          {label: 'My Tasks', attn: (attnNums?.grantAssignmentsCount || 0), href: paths.cadminGrantMyTasks(slug), active: is('grant-my-tasks')},
          {label: 'Programs', href: paths.cadminGrantPrograms(slug), active: is('grant-programs')},
          {label: 'Budgets', href: paths.cadminGrantBudgets(slug), active: is('grant-budgets')},
          {label: 'Library', href: paths.cadminGrantSysSettings(slug), active: is('grant-settings')},
        ]},        
      ] : []),
      ...(company.features.gift ? [
        {label: 'Gifts', Icon: Icon.GiftBox, children: [
          {label: 'Send Gift', href: paths.cadminGiftsSend(slug), active: is('gifts-send')},
          {label: 'Past Gifts', href: paths.cadminGifts(slug), active: is('gifts')},
          {label: 'Custom Cards', href: paths.cadminCards(slug), active: is('custom-cards')},
        ]},
      ] : []),
      ...(company.features.groups ? [
        {label: 'Groups', Icon: Icon.TeamworkHands, children: [
          {label: 'Groups', attn: (attnNums?.pendingGroups || 0), href: paths.cadminGroups(slug), active: is('groups'), roles: [Roles.GROUP_LEAD]},
          {label: 'Members', href: paths.cadminGroupEmployees(slug), active: is('groups-members'), roles: [Roles.GROUP_LEAD]},
          {label: 'Events', attn: (attnNums?.pendingGroupEvents || 0), href: paths.cadminVolEvents(slug, {type: VolEventTypes.EVENT}), active: is('events'), roles: [Roles.GROUP_LEAD]},
          {label: 'Event Attendees', href: paths.cadminVolTracking(slug, {eventType: VolEventTypes.EVENT}), active: is('event-tracking'), roles: [Roles.GROUP_LEAD]},
        ]},
      ] : []),
      ...(company.features.madness ? [
        {label: 'Madness', Icon: Icon.Bracket, children: [
          {label: 'Brackets', href: paths.cadminBrackets(slug), active: is('brackets'), roles: [Roles.CHAMPION, Roles.GROUP_LEAD]},
          {label: 'Analytics', href: paths.cadminAnalytics(slug, {}, gmPreset.key), active: is(`anal-${gmPreset.key}`)},
        ]},
      ] : []),
      {label: 'Billing', Icon: Icon.ReceiptSlip1, children: [
        {label: 'Wallet', href: paths.cadminWallet(slug), active: is('wallet'), roles: [Roles.FINANCE]},
        {label: 'Transactions', href: paths.cadminTransactions2(slug), active: is('transactions'), roles: [Roles.FINANCE]},
        {label: 'Upcoming Charges', href: paths.cadminUpcomingCharges(slug), active: is('upcoming-charges'), roles: [Roles.FINANCE]},
        ...(company.features.payroll ? [
          {label: 'Payroll Giving', href: paths.cadminPayroll(slug), active: is('payroll'), roles: [Roles.FINANCE]},
        ] : []),
        {label: 'Notifications', href: paths.cadminBillingNotifications(slug), active: is('billing-notifications'), roles: [Roles.FINANCE]},
      ]},
      {label: 'Settings', Icon: Icon.Cog1, children: [
        {label: 'Employees', href: paths.cadminEmployees(slug), active: is('employees'), roles: [Roles.IT]},
        {label: 'Approvals', href: paths.cadminSettingsApprovals(slug), active: is('settings-approvals')},
        {label: 'Integrations', href: paths.cadminSlackSettings(slug), active: is('integrations'), roles: [Roles.IT]},
        ...(company.features.sso ? [
          {label: 'SSO', href: paths.cadminSso(slug), active: is('sso'), roles: [Roles.IT]},
        ] : []),
        ...(ffCattrs ? [
          {label: 'Custom Attributes', href: paths.cadminCattrs(slug), active: is('cattrs'), roles: [Roles.IT]},
        ] : []),
        ...(showSubx ? [
          {label: 'Subscription', href: paths.cadminSubscription(slug), active: is('subscription')},
        ] : []),
      ]},
    ]
    // filter based on employee.role
    return menuItems
      .map((menuItem) => {
        if (role === Roles.ADMIN) return menuItem;
        return {
          ...menuItem,
          children: menuItem.children.filter((child) => (child.roles || []).includes(role)),
        };
      })
      .filter((menuItem) => {
        if (role === Roles.ADMIN) return true;
        return menuItem.children.length;
      });
  }

  onClickBurger() {
    // this.setState((prevState) => ({
    //   pinned: !prevState.pinned,
    // }));
  }

  render() {
    const { company, activeItem, currentUser } = this.props;
    const role = _.get(currentUser, 'employment.employee.role', Roles.EMPLOYEE);
    const { pinned } = this.state;
    const menuItems = this.getMenuItems();
    const expandAll = role !== Roles.ADMIN;

    return (
      <div className={`cadmin-nav ${pinned ? 'pinned': 'unpinned'} ${expandAll ? 'expand-all' : ''}`}>
        <div className="cadmin-nav-tab" onClick={this.onClickBurger}>
          <Icon.Caret direction="down" />
          <span>Menu</span>
        </div>
        {/* <div className="cadmin-nav-top"> */}
        {/*   <img className="cadmin-nav-top-logo" src={company.logoUrl} alt={company.name} /> */}
        {/* </div> */}
        <div className="cadmin-nav-menu">
          {menuItems.map((menuItem) => {
            const activeClass = menuItem.children.some(c => c.active) ? 'active' : '';
            const soonClass = menuItem.comingSoon ? 'coming-soon' : '';
            const attn = _.sum(menuItem.children.map(child => child.attn)) || 0;
            return (
              <div className={`cadmin-nav-menu-group ${activeClass} ${soonClass}`} key={menuItem.label}>
                <div className="cadmin-nav-menu-group-item">
                  <menuItem.Icon />
                  {menuItem.label}
                  {!!attn && <span className="cadmin-nav-menu-attn">{attn}</span>}
                </div>
                <div className={`cadmin-nav-menu-group-children n${menuItem.children.length}`}>
                  {menuItem.children.map((childItem) => {
                    const activeClass2 = childItem.active ? 'active' : '';
                    const soonClass2 = childItem.comingSoon ? 'coming-soon' : '';
                    return (
                      <Link href={childItem.href || '#'} className={`cadmin-nav-menu-group-child ${activeClass2} ${soonClass2}`} key={childItem.label}>
                        {childItem.label}
                        {!!childItem.attn && <span className="cadmin-nav-menu-attn">{childItem.attn}</span>}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div className="cadmin-nav-menu-bottom-spacer" />
        </div>
        {!this.isGmOnly && (
          <div className="cadmin-nav-bottom">
            <Link href={paths.companyDashboard(company.companyDashboardId)} className="btn special jungle cadmin-nav-bottom-db-btn">Team Dashboard</Link>
          </div>
        )}
      </div>
    );
  }

}

CadminNav.propTypes = {
  company: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  activeItem: PropTypes.string,
};

const stateToProps = (state) => ({
  ffCattrs: FfSlx['cattrs'](state),
  ffGrants: FfSlx['grants'](state),
  attnNums: CadminSlx.attnNums(state),
});

export default connect(stateToProps)(CadminNav);
